import { useNavigate } from '@tanstack/react-router';
import { Car, Heart, Home, Shield } from 'lucide-react';

import type { InsuranceType } from '../context/InsuranceTypeContext';
import { useInsuranceType } from '../hooks/useInsuranceType';
import { useTranslation } from '../hooks/useTranslation';
import { toast } from '../lib/toast';

type ServiceItem = {
  id: string;
  insuranceType: InsuranceType;
  icon: React.ReactNode;
  title: string;
  description: string;
  disabled: boolean;
};

const FormSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSelectedType } = useInsuranceType();

  const services: ServiceItem[] = [
    {
      id: 'auto',
      insuranceType: 'auto',
      icon: <Car className="h-7 w-7 text-accent-primary" />,
      title: t('home.services.auto.title'),
      description: t('home.services.auto.description'),
      disabled: false,
    },
    {
      id: 'life',
      insuranceType: 'life',
      icon: <Heart className="h-7 w-7 text-accent-primary" />,
      title: t('home.services.life.title'),
      description: t('home.services.life.description'),
      disabled: true,
    },
    {
      id: 'home',
      insuranceType: 'home',
      icon: <Home className="h-7 w-7 text-accent-primary" />,
      title: t('home.services.home.title'),
      description: t('home.services.home.description'),
      disabled: false,
    },
    {
      id: 'disability',
      insuranceType: 'disability',
      icon: <Shield className="h-7 w-7 text-accent-primary" />,
      title: t('home.services.disability.title'),
      description: t('home.services.disability.description'),
      disabled: true,
    },
  ];

  const handleServiceClick = (service: ServiceItem) => {
    if (service.disabled) {
      toast.info('toast.comingSoon', {
        messageParams: { service: service.title },
      });
      return;
    }
    if (!service.insuranceType) return;
    setSelectedType(service.insuranceType);
    navigate({ to: '/consent' });
  };

  return (
    <div className="flex min-h-[calc(100vh-80px)] items-center justify-center bg-surface-primary px-6 py-16 pt-24 lg:px-20 lg:py-25">
      <div className="w-full max-w-6xl">
        <h1 className="mb-12 text-center font-heading text-3xl font-medium text-foreground-primary lg:text-5xl">
          {t('home.services.title')}
        </h1>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {services.map((service) => (
            <button
              key={service.id}
              onClick={() => handleServiceClick(service)}
              disabled={service.disabled}
              className="flex cursor-pointer flex-col gap-6 rounded-lg bg-surface-card p-8 text-left shadow-card transition-shadow hover:shadow-card-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-primary/10">
                {service.icon}
              </div>
              <h3 className="font-heading text-xl font-medium text-foreground-primary">
                {service.title}
              </h3>
              <p className="font-body text-sm leading-relaxed text-foreground-secondary">
                {service.description}
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FormSelection;
